import { useApolloClient, useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { gql } from "generated-graphql";
import { useCallback } from "react";

import type { InboundEmailRow } from "./senderDialogValues";

const DISMISS_EMAIL = gql(`
  mutation DismissReceivedEmail($id: ID!, $dismissed: Boolean!) {
    dismissReceivedEmail(id: $id, dismissed: $dismissed)
  }
`);

/**
 * Mark an inbound email as needing no action, or take that back.
 *
 * No confirm: dismissing is reversible from the same row, and the needs-attention
 * view is where the person already is. Both inbound queries are evicted, as for a
 * replay — the row and the opened detail panel read from different cache fields.
 */
export function useDismissReceivedEmail() {
  const alerts = useAlerts();
  const apolloClient = useApolloClient();
  const [dismissEmail] = useMutation(DISMISS_EMAIL);

  return useCallback(
    (row: InboundEmailRow, dismissed: boolean) => {
      void (async () => {
        try {
          await dismissEmail({ variables: { id: row.id, dismissed } });
          alerts.success(
            dismissed
              ? "Dismissed. Filter on Dismissed to see it again."
              : "Restored to the needs-attention view."
          );
          apolloClient.cache.evict({ fieldName: "receivedEmails" });
          apolloClient.cache.evict({ fieldName: "receivedEmail" });
          apolloClient.cache.gc();
        } catch (err) {
          alerts.error(err instanceof Error ? err.message : "Couldn't update that email.", err);
        }
      })();
    },
    [alerts, apolloClient, dismissEmail]
  );
}
